import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { RailFrame, AdminWindowSwitcher } from '../components/AdminShell';
import '../components/lists/Lists.css';
import './MarketingPages.css';

// Queue chips (same words as the dashboard): green done, amber running, neutral waiting, red failed.
const QUEUE_CHIP = {
  completed: ['Done', 'adm-chip--green'],
  scraping: ['In progress', 'adm-chip--amber'], discovering: ['In progress', 'adm-chip--amber'], teams_discovered: ['In progress', 'adm-chip--amber'],
  approved: ['Queued', ''], pending: ['Pending', ''], failed: ['Failed', 'adm-chip--red'],
};

export default function MarketingSchoolsPage() {
  const { API_URL } = useAuth();
  const MARKETING_URL = `${API_URL.replace('/api', '')}/marketing`;
  const [searchParams, setSearchParams] = useSearchParams();
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  // School browser state
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  // Queue management state
  const [queue, setQueue] = useState([]);
  const [actionMsg, setActionMsg] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState('scraped');         // scraped | queue
  const [indexFilter, setIndexFilter] = useState('');
  const searchTimeout = useRef(null);
  const addRef = useRef(null);
  const fileInputRef = useRef(null);

  const showMsg = (msg) => {
    setActionMsg(msg);
    setTimeout(() => setActionMsg(''), 4000);
  };

  const fetchSchools = () =>
    axios.get(`${MARKETING_URL}/schools`, { headers })
      .then(res => setSchools(res.data))
      .catch(err => console.error('Error loading schools:', err));

  const fetchQueue = () =>
    axios.get(`${MARKETING_URL}/scrape/queue`, { headers })
      .then(res => setQueue(res.data))
      .catch(err => console.error('Error loading queue:', err));

  useEffect(() => {
    Promise.all([fetchSchools(), fetchQueue()]).finally(() => setLoading(false));
  }, []);

  // The selected school lives in ?school=ID (the admin search links here too). A missing or stale id
  // falls back to the first scraped school. Loading its teams (and the first team's roster) follows.
  const schoolParam = Number(searchParams.get('school')) || null;
  const targetSchool = schools.find((sc) => sc.id === schoolParam) || schools[0] || null;
  const targetId = targetSchool?.id;
  useEffect(() => {
    if (targetSchool && targetSchool.id !== selectedSchool?.id) loadTeams(targetSchool);
  }, [targetId]);

  // ?add=1 (the dashboard's "+ Queue school") focuses the add box.
  const wantsAdd = searchParams.get('add') === '1';
  useEffect(() => {
    if (!loading && wantsAdd) addRef.current?.focus();
  }, [loading, wantsAdd]);

  // ---- Search ----
  const handleSearch = (value) => {
    setSearchInput(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (value.length < 2) { setSearchResults([]); return; }
    const delay = value.includes('hudl.com') ? 100 : 500;
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${MARKETING_URL}/scrape/search`, { headers, params: { q: value } });
        setSearchResults(res.data);
      } catch { setSearchResults([]); }
      setSearching(false);
    }, delay);
  };

  const addSchool = async (result) => {
    setAdding(result.hudl_org_id);
    try {
      await axios.post(`${MARKETING_URL}/scrape/queue`, {
        school_name: result.name,
        hudl_url: result.hudl_url,
        hudl_org_id: result.hudl_org_id,
      }, { headers });
      showMsg(`Added "${result.name}" to queue`);
      setSearchInput('');
      setSearchResults([]);
      fetchQueue();
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Error adding school');
    }
    setAdding(null);
  };

  const isInQueue = (orgId) => queue.some(q => q.hudl_org_id === orgId);

  // ---- Upload list ----
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      if (lines.length === 0) { showMsg('File is empty'); setUploading(false); return; }

      // Detect header row
      const firstLower = lines[0].toLowerCase();
      const hasHeader = firstLower.includes('school') || firstLower.includes('name');
      const dataLines = hasHeader ? lines.slice(1) : lines;

      // Parse columns: school_name[,hudl_url[,hudl_org_id]]
      const items = dataLines
        .map(line => {
          const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
          const school_name = cols[0];
          const hudl_url = cols[1] || null;
          const hudl_org_id = cols[2] || null;
          return school_name ? { school_name, hudl_url, hudl_org_id } : null;
        })
        .filter(Boolean);

      if (items.length === 0) { showMsg('No valid schools found in file'); setUploading(false); return; }

      const res = await axios.post(`${MARKETING_URL}/scrape/queue/batch`, items, { headers });
      showMsg(res.data.message || `Uploaded ${items.length} schools`);
      fetchQueue();
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
    e.target.value = '';
  };

  // ---- Download sample ----
  const downloadSample = () => {
    const rows = [
      'school_name,hudl_url,hudl_org_id',
      'Glenbrook South High School,,',
      'Lake Forest High School,https://fan.hudl.com/usa/il/lake-forest/organization/2259/lake-forest-high-school,2259',
      'Naperville North High School,,',
    ].join('\r\n');
    const blob = new Blob([rows], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'school_upload_sample.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // ---- Queue actions ----
  const approve = async (id) => {
    try {
      await axios.post(`${MARKETING_URL}/scrape/queue/${id}/approve`, {}, { headers });
      showMsg('School approved for scraping');
      fetchQueue();
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Error approving school');
    }
  };

  const approveAll = async () => {
    try {
      const res = await axios.post(`${MARKETING_URL}/scrape/queue/approve-all`, {}, { headers });
      showMsg(res.data.message);
      fetchQueue();
    } catch (err) {
      showMsg(err.response?.data?.detail || 'Error approving all');
    }
  };

  const pendingCount = queue.filter(q => q.status === 'pending' || q.status === 'failed').length;

  // Queue sort state
  const [queueSortCol, setQueueSortCol] = useState('name');
  const [queueSortDir, setQueueSortDir] = useState('asc');

  const STATUS_ORDER = { pending: 0, failed: 1, approved: 2, discovering: 3, teams_discovered: 4, scraping: 5, completed: 6 };

  const handleQueueSort = (col) => {
    if (queueSortCol === col) {
      setQueueSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setQueueSortCol(col);
      setQueueSortDir('asc');
    }
  };

  const queueSortIndicator = (col) => {
    if (queueSortCol === col) return queueSortDir === 'asc' ? ' ↑' : ' ↓';
    return ' ↕';
  };

  const getSortedQueue = () => {
    return [...queue].sort((a, b) => {
      let aVal, bVal;
      if (queueSortCol === 'name') {
        aVal = (a.school_name || '').toLowerCase();
        bVal = (b.school_name || '').toLowerCase();
      } else {
        // status sort — also used for action column
        aVal = STATUS_ORDER[a.status] ?? 99;
        bVal = STATUS_ORDER[b.status] ?? 99;
      }
      if (aVal < bVal) return queueSortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return queueSortDir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // ---- School browser ----
  const loadTeams = async (school) => {
    setSelectedSchool(school);
    setSelectedTeam(null);
    setPlayers([]);
    setTeams([]);
    try {
      const res = await axios.get(`${MARKETING_URL}/schools/${school.id}/teams`, { headers });
      setTeams(res.data);
      if (res.data.length > 0) loadPlayers(res.data[0]);
    } catch (err) { console.error('Error loading teams:', err); }
  };

  const loadPlayers = async (team) => {
    setSelectedTeam(team);
    try {
      const res = await axios.get(`${MARKETING_URL}/schools/teams/${team.id}/players`, { headers });
      setPlayers(res.data);
    } catch (err) { console.error('Error loading players:', err); }
  };

  const viewPlayerDetail = async (playerId) => {
    try {
      const res = await axios.get(`${MARKETING_URL}/schools/players/${playerId}`, { headers });
      setSelectedPlayer(res.data);
    } catch (err) { console.error('Error loading player detail:', err); }
  };

  const downloadCSV = async () => {
    if (!selectedTeam) return;
    setDownloading(true);
    try {
      const res = await axios.get(
        `${MARKETING_URL}/schools/teams/${selectedTeam.id}/players/csv`,
        { headers, responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${(selectedTeam.name || 'team').replace(/\s+/g, '_')}_roster.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) { console.error('CSV download error:', err); }
    setDownloading(false);
  };

  const pickSchool = (id) => { setMode('scraped'); setSearchParams({ school: String(id) }, { replace: true }); };
  const q = indexFilter.trim().toLowerCase();
  const shownSchools = schools.filter((sc) => !q || (sc.name || '').toLowerCase().includes(q));
  const queueRows = getSortedQueue().filter((r) => !q || (r.school_name || '').toLowerCase().includes(q));
  const place = (sc) => [sc.city, sc.state].filter(Boolean).join(', ');
  const pos = (p) => (Array.isArray(p.position) ? p.position.join(', ') : p.position) || '-';

  const modalJsx = (
    <>
      {selectedPlayer && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setSelectedPlayer(null)}>
          <div className="modal-panel" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '85vh', overflow: 'auto' }}>
            <button className="modal-close" onClick={() => setSelectedPlayer(null)}>&times;</button>

            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '20px' }}>
              {selectedPlayer.profile_photo_url && (
                <img src={selectedPlayer.profile_photo_url} alt={selectedPlayer.full_name}
                  style={{ width: '64px', height: '64px', borderRadius: '50%', objectFit: 'cover' }} />
              )}
              <div>
                <div className="modal-title" style={{ marginBottom: '2px' }}>{selectedPlayer.full_name}</div>
                <div style={{ fontSize: '13px', color: '#64748b' }}>
                  {selectedPlayer.school?.name}
                  {selectedPlayer.school?.city && ` - ${selectedPlayer.school.city}, ${selectedPlayer.school.state}`}
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px', marginBottom: '20px' }}>
              {[
                ['Position', Array.isArray(selectedPlayer.position) ? selectedPlayer.position.join(', ') : selectedPlayer.position],
                ['Grad Year', selectedPlayer.graduation_year],
                ['Height', selectedPlayer.height],
                ['Weight', selectedPlayer.weight ? `${selectedPlayer.weight} lbs` : null],
                ['Jersey', selectedPlayer.jersey_number],
                ['H/W Verified', selectedPlayer.height_weight_verified ? 'Yes' : null],
              ].map(([label, value]) => value ? (
                <div key={label}><span style={{ color: '#64748b' }}>{label}:</span> <strong>{value}</strong></div>
              ) : null)}
            </div>

            {selectedPlayer.bio && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>Bio</div>
                <div style={{ fontSize: '13px', color: '#475569', lineHeight: 1.5 }}>{selectedPlayer.bio}</div>
              </div>
            )}

            {selectedPlayer.social_links?.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '6px' }}>Social Media</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {selectedPlayer.social_links.map((s, i) => (
                    <span key={i} style={{ padding: '4px 12px', background: '#f1f5f9', borderRadius: '16px', fontSize: '13px', fontWeight: 500 }}>
                      {s.platform}: @{s.handle || '-'}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {selectedPlayer.engagement && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '6px' }}>Hudl Engagement</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', fontSize: '13px' }}>
                  {selectedPlayer.engagement.followers != null && <div><span style={{ color: '#64748b' }}>Followers:</span> <strong>{selectedPlayer.engagement.followers}</strong></div>}
                  {selectedPlayer.engagement.following != null && <div><span style={{ color: '#64748b' }}>Following:</span> <strong>{selectedPlayer.engagement.following}</strong></div>}
                  {selectedPlayer.engagement.video_views != null && <div><span style={{ color: '#64748b' }}>Video Views:</span> <strong>{selectedPlayer.engagement.video_views.toLocaleString()}</strong></div>}
                  {selectedPlayer.engagement.recruiter_video_views != null && <div><span style={{ color: '#64748b' }}>Recruiter Views:</span> <strong>{selectedPlayer.engagement.recruiter_video_views}</strong></div>}
                  {selectedPlayer.engagement.highlight_reels != null && <div><span style={{ color: '#64748b' }}>Highlight Reels:</span> <strong>{selectedPlayer.engagement.highlight_reels}</strong></div>}
                </div>
              </div>
            )}

            {selectedPlayer.highlights?.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '6px' }}>Highlight Videos</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {selectedPlayer.highlights.map((h, i) => (
                    <a key={i} href={h.hudl_url} target="_blank" rel="noopener noreferrer"
                      style={{ display: 'flex', gap: '10px', padding: '8px', borderRadius: '8px', background: '#f8fafc', textDecoration: 'none', color: 'inherit', border: h.is_featured ? '2px solid #f59e0b' : '1px solid #e5e7eb' }}>
                      {h.thumbnail_url && <img src={h.thumbnail_url} alt={h.title} style={{ width: '80px', height: '50px', borderRadius: '4px', objectFit: 'cover' }} />}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: 600 }}>
                          {h.title}{h.is_featured && <span style={{ color: '#f59e0b', marginLeft: '6px', fontSize: '11px' }}>FEATURED</span>}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                          {h.clip_count && <span>{h.clip_count}</span>}
                          {h.duration_seconds && <span> | {Math.floor(h.duration_seconds / 60)}:{String(Math.floor(h.duration_seconds % 60)).padStart(2, '0')}</span>}
                          {h.season && <span> | {h.season} season</span>}
                          {h.total_views != null && <span> | {h.total_views.toLocaleString()} views</span>}
                        </div>
                      </div>
                      <span style={{ color: '#3b82f6', fontSize: '12px', alignSelf: 'center' }}>&rarr;</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {selectedPlayer.athletic_metrics?.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '6px' }}>Athletic Testing</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', fontSize: '13px' }}>
                  {selectedPlayer.athletic_metrics.map((m, i) => (
                    <div key={i}>
                      <span style={{ color: '#64748b' }}>{m.name}:</span>{' '}
                      <strong>{m.value} {m.unit}</strong>
                      {m.verified && <span style={{ color: '#3b82f6', fontSize: '11px', marginLeft: '4px' }}>✓</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedPlayer.academics && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '6px' }}>Academics</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', fontSize: '13px' }}>
                  {selectedPlayer.academics.gpa && <div><span style={{ color: '#64748b' }}>GPA:</span> <strong>{selectedPlayer.academics.gpa}</strong></div>}
                  {selectedPlayer.academics.sat_score && <div><span style={{ color: '#64748b' }}>SAT:</span> <strong>{selectedPlayer.academics.sat_score}</strong></div>}
                  {selectedPlayer.academics.act_score && <div><span style={{ color: '#64748b' }}>ACT:</span> <strong>{selectedPlayer.academics.act_score}</strong></div>}
                  {selectedPlayer.academics.class_level && <div><span style={{ color: '#64748b' }}>Class:</span> <strong>{selectedPlayer.academics.class_level}</strong></div>}
                </div>
              </div>
            )}

            {selectedPlayer.teams?.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '6px' }}>Teams</div>
                {selectedPlayer.teams.map((t, i) => (
                  <div key={i} style={{ fontSize: '13px', color: '#475569', marginTop: '4px' }}>
                    {t.team_name} ({t.sport}) {t.team_level && `- ${t.team_level}`}
                  </div>
                ))}
              </div>
            )}

            {selectedPlayer.hudl_profile_url && (
              <a href={selectedPlayer.hudl_profile_url} target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-block', marginTop: '8px', fontSize: '13px', color: '#3b82f6', fontWeight: 500 }}>
                View on Hudl &rarr;
              </a>
            )}

            <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #f1f5f9', fontSize: '12px', color: '#94a3b8' }}>
              First scraped: {selectedPlayer.first_scraped_at ? new Date(selectedPlayer.first_scraped_at).toLocaleDateString() : '-'}
              {' | '}Last scraped: {selectedPlayer.last_scraped_at ? new Date(selectedPlayer.last_scraped_at).toLocaleDateString() : '-'}
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (loading) return <RailFrame active="mkt-schools"><p className="adm-loading">Loading…</p></RailFrame>;

  return (
    <RailFrame active="mkt-schools">
      {actionMsg && <div className="ls-banner" role="status">{actionMsg}</div>}
      <div className="ls-grid">
        <aside className="adm__card ls-index">
          <div className="ls-index__head">
            <h2 className="ls-index__title">Schools</h2>
          </div>

          <div className="mkt-add">
            <input ref={addRef} className="ls-find mkt-add__input" type="text" aria-label="Add a school"
              placeholder="School name or hudl URL…" value={searchInput} onChange={(e) => handleSearch(e.target.value)} />
            {searching && <span className="mkt-add__note">Searching…</span>}
            {searchResults.length > 0 && (
              <ul className="mkt-results">
                {searchResults.map((r, i) => (
                  <li key={r.hudl_org_id || i} className="mkt-result">
                    <span className="mkt-result__text">
                      <span className="mkt-school">{r.name}</span>
                      <span className="mkt-sub">{[r.city, r.state].filter(Boolean).join(', ') || 'Location unknown'}</span>
                    </span>
                    {isInQueue(r.hudl_org_id)
                      ? <span className="mkt-sub">In queue</span>
                      : <button type="button" className="ls-btn ls-btn--primary ls-btn--tiny" disabled={adding === r.hudl_org_id}
                          onClick={() => addSchool(r)}>{adding === r.hudl_org_id ? 'Adding…' : 'Add to queue'}</button>}
                  </li>
                ))}
              </ul>
            )}
            {searchInput.length >= 2 && !searching && searchResults.length === 0 && <span className="mkt-add__note">No results found.</span>}
            <div className="mkt-add__tools">
              <button type="button" className="srch-link" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                {uploading ? 'Uploading…' : '↑ Upload list'}
              </button>
              <button type="button" className="srch-link" onClick={downloadSample}>↓ Sample format</button>
              <input ref={fileInputRef} type="file" accept=".csv,.txt" className="mkt-file" aria-label="Upload school list" onChange={handleUpload} />
            </div>
          </div>

          <div className="mkt-switch">
            <AdminWindowSwitcher label="Show" value={mode} onChange={setMode}
              options={[{ value: 'scraped', label: `Scraped ${schools.length}` }, { value: 'queue', label: `Queue ${queue.length}` }]} />
          </div>
          <input className="ls-find" type="search" placeholder={mode === 'queue' ? 'Find in queue…' : 'Find a school…'} aria-label="Find"
            value={indexFilter} onChange={(e) => setIndexFilter(e.target.value)} />

          {mode === 'scraped' ? (
            shownSchools.length === 0
              ? <p className="ls-empty">{schools.length === 0 ? 'No schools scraped yet. Add schools to the queue above.' : 'No school matches.'}</p>
              : shownSchools.map((sc) => (
                <button key={sc.id} type="button" className={`ls-irow ${selectedSchool?.id === sc.id ? 'ls-irow--active' : ''}`}
                  aria-current={selectedSchool?.id === sc.id ? 'true' : undefined} onClick={() => pickSchool(sc.id)}>
                  <span className="ls-irow__text">
                    <span className="ls-irow__title">{sc.name}</span>
                    <span className="ls-irow__sub">{`${place(sc) || '-'} · ${sc.teams_count || 0} teams · ${(sc.players_count || 0).toLocaleString()} players`}</span>
                  </span>
                </button>
              ))
          ) : (
            <p className="ls-empty">The queue is shown on the right.</p>
          )}
        </aside>

        <section className="adm__card ls-open">
          {mode === 'queue' ? (
            <>
              <div className="ls-head">
                <div className="ls-head__main">
                  <div className="ls-head__meta"><span className="ls-chip">Queue</span><span className="ls-head__hint">{queue.length} schools</span></div>
                  <h2 className="wb-title">School queue</h2>
                </div>
                <div className="ls-head__actions">
                  {pendingCount > 0 && (
                    <button type="button" className="ls-btn ls-btn--primary" onClick={approveAll}>{`Approve all pending (${pendingCount})`}</button>
                  )}
                </div>
              </div>
              {queueRows.length === 0 ? <p className="adm-empty">{queue.length === 0 ? 'No schools in queue.' : 'No school matches.'}</p> : (
                <div className="adm-tablewrap">
                  <table className="adm-table">
                    <thead>
                      <tr>
                        <th className="adm-th--sortable" onClick={() => handleQueueSort('name')}>{`SCHOOL${queueSortIndicator('name')}`}</th>
                        <th className="adm-th--sortable" onClick={() => handleQueueSort('status')}>{`STATUS${queueSortIndicator('status')}`}</th>
                        <th>ACTION</th>
                      </tr>
                    </thead>
                    <tbody>
                      {queueRows.map((r) => {
                        const [label, tone] = QUEUE_CHIP[r.status] || [r.status, ''];
                        return (
                          <tr key={r.id}>
                            <td>
                              {r.school_id && schools.some((sc) => sc.id === r.school_id)
                                ? <button type="button" className="mkt-link" onClick={() => pickSchool(r.school_id)}>{r.school_name}</button>
                                : <span className="mkt-school">{r.school_name}</span>}
                            </td>
                            <td><span className={`adm-chip ${tone}`}>{label}</span></td>
                            <td className="mkt-actions">
                              {(r.status === 'pending' || r.status === 'failed') && (
                                <button type="button" className="adm-btn adm-btn--sm adm-btn--primary" onClick={() => approve(r.id)}>Approve</button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : !selectedSchool ? (
            <p className="adm-empty ls-empty--pane">Add a school to the queue to start collecting rosters.</p>
          ) : (
            <>
              <div className="ls-head">
                <div className="ls-head__main">
                  <div className="ls-head__meta">
                    <span className="ls-chip">Scraped</span>
                    <span className="ls-head__hint">{`${place(selectedSchool) || '-'} · ${selectedSchool.teams_count || 0} teams · ${(selectedSchool.players_count || 0).toLocaleString()} players`}</span>
                  </div>
                  <h2 className="wb-title">{selectedSchool.name}</h2>
                </div>
                <div className="ls-head__actions">
                  <button type="button" className="ls-btn ls-btn--dark" onClick={downloadCSV} disabled={downloading || players.length === 0}>
                    {downloading ? 'Downloading…' : 'Export roster'}
                  </button>
                </div>
              </div>

              {teams.length > 1 && (
                <div className="mkt-teams">
                  <AdminWindowSwitcher label="Team" value={selectedTeam?.id} onChange={(id) => loadPlayers(teams.find((t) => t.id === id))}
                    options={teams.map((t) => ({ value: t.id, label: t.name }))} />
                </div>
              )}

              {teams.length === 0 ? <p className="adm-empty">No teams found.</p>
                : players.length === 0 ? <p className="adm-empty">No players found.</p> : (
                <div className="adm-tablewrap">
                  <table className="adm-table">
                    <thead>
                      <tr><th>NAME</th><th>POS</th><th>GRAD</th><th>HT</th><th>WT</th><th>#</th><th>SOCIAL</th></tr>
                    </thead>
                    <tbody>
                      {players.map((p) => (
                        <tr key={p.id} className="mkt-row" onClick={() => viewPlayerDetail(p.id)}>
                          <td className="mkt-school">
                            {p.full_name}
                            {p.profile_scraped && <span title="Profile scraped" className="mkt-dot">&#9679;</span>}
                            {p.has_highlights && <span title="Has highlight videos" className="mkt-play">&#9654;</span>}
                          </td>
                          <td>{pos(p)}</td>
                          <td>{p.graduation_year || '-'}</td>
                          <td>{p.height || '-'}</td>
                          <td>{p.weight || '-'}</td>
                          <td>{p.jersey_number || '-'}</td>
                          <td>{p.has_social ? <span className="adm-chip adm-chip--green">Yes</span> : <span className="mkt-muted">No</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>
      </div>
      {modalJsx}
    </RailFrame>
  );
}
